import { useCallback, useMemo } from 'react'

import {
  AsyncSelect,
  type IAsyncSelectProps,
  Space,
  SubText,
} from '@procurify/ui'
import {
  components as Components,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { UserAPI } from '@webapp/api/UserAPI'
import { toast } from '@webapp/components/Notification'
import { CreditCardIcon } from '@webapp/core/components/CreditCardIcon'
import { CreditCardVendors } from '@webapp/core/entities/external/CreditCardVendor'
import { useProcurifyIntl } from '@webapp/hooks'
import { type User } from '@webapp/models'
import { type SimpleAPCreditCard } from '@webapp/models/APCreditCard'
import messages from './messages'

export const OTHER_PAYMENT_OPTION_ID = -1 as const

export interface OtherPaymentOption {
  id: typeof OTHER_PAYMENT_OPTION_ID
  name: string
  number: string
  type: null
  currency: null
  vendor: null
}

export type RequestableCreditCardOption =
  | SimpleAPCreditCard
  | OtherPaymentOption

export interface RequestableCreditCardSelectProps extends Omit<
  IAsyncSelectProps<RequestableCreditCardOption>,
  'loadOptions' | 'components'
> {
  userId: User['id']
}

const isOtherOption = (
  option: RequestableCreditCardOption
): option is OtherPaymentOption => option.id === OTHER_PAYMENT_OPTION_ID

const SingleValue = (
  props: SingleValueProps<RequestableCreditCardOption, false>
) => {
  return (
    <Components.SingleValue {...props}>
      <Space direction='horizontal' align='center' size='small'>
        <CreditCardIcon
          type={
            isOtherOption(props.data)
              ? CreditCardVendors.OTHER
              : props.data.type
          }
          height={14}
        />
        <Space align='center'>
          <SubText inline textEllipsis autoMargin={false}>
            {props.data.name}
          </SubText>
        </Space>
      </Space>
    </Components.SingleValue>
  )
}

const Option = (props: OptionProps<RequestableCreditCardOption, false>) => {
  const intl = useProcurifyIntl()

  const isOther = isOtherOption(props.data)
  const iconType = isOther ? CreditCardVendors.OTHER : props.data.type
  const description = isOther
    ? props.data.number
    : intl.formatMessage(messages.endingIn, {
        cardNumber: props.data.number,
      })

  return (
    <Components.Option {...props}>
      <Space direction='horizontal' align='center'>
        <Space>
          <CreditCardIcon type={iconType} height={24} />
        </Space>
        <Space size='xsmall'>
          <SubText inline textEllipsis autoMargin={false}>
            {props.data.name}
          </SubText>
          <SubText>{description}</SubText>
        </Space>
      </Space>
    </Components.Option>
  )
}

export const RequestableCreditCardSelect = ({
  userId,
  ...props
}: RequestableCreditCardSelectProps) => {
  const intl = useProcurifyIntl()

  const otherOption = useMemo<OtherPaymentOption>(
    () => ({
      id: OTHER_PAYMENT_OPTION_ID,
      name: intl.formatMessage(messages.otherName),
      number: intl.formatMessage(messages.otherDescription),
      type: null,
      currency: null,
      vendor: null,
    }),
    [intl]
  )

  const loadOptions = useCallback<
    IAsyncSelectProps<RequestableCreditCardOption>['loadOptions']
  >(
    async (inputValue, pagination, callback) => {
      try {
        const res = await UserAPI.getUserCreditCards(userId, {
          ...(inputValue && { search: inputValue }),
          page: pagination.page,
        })

        const hasMore = !!res.metadata?.pagination?.next
        const otherOptionMatchesSearch =
          !inputValue ||
          otherOption.name.toLowerCase().includes(inputValue.toLowerCase())
        const options: RequestableCreditCardOption[] = [
          ...res.data,
          ...(!hasMore && otherOptionMatchesSearch ? [otherOption] : []),
        ]

        callback({
          options,
          pagination: {
            hasMore,
            page: pagination.page + 1,
          },
        })
      } catch {
        toast.error({
          title: intl.formatMessage(messages.loadErrorTitle),
          children: intl.formatMessage(messages.loadErrorMessage),
        })
        callback({ options: [], pagination: { hasMore: false, page: 1 } })
      }
    },
    [userId, otherOption, intl]
  )

  return (
    <AsyncSelect<RequestableCreditCardOption>
      components={{
        SingleValue,
        Option,
      }}
      placeholder={intl.formatMessage(messages.placeholder)}
      loadOptions={loadOptions}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      {...props}
    />
  )
}

RequestableCreditCardSelect.displayName = 'RequestableCreditCardSelect'
